import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useUserInfoContext } from "@/contexts";
// import { mockInvoiceList } from "@/MockData/invoice";
import authAxiosInstance from "@/sevices/authAxiosInstance";
import {
  CreditList,
  CreditSortableColumn,
  CreditSortableColumnKeys
} from "@/types/Credit";
import {
  InvoiceList,
  InvoicesAndPaymentsTabs,
  PageSize,
  SortableColumn,
  SortableColumnKeys,
  SortOrder
} from "@/types/Invoice";
import { ErrorTypes } from "@/utils/Errors/errorMessages";
import { getErrorMessage } from "@/utils/Errors/getErrorMessage";

function useQueryParams() {
  const { search, pathname } = useLocation();
  const history = useHistory();

  const query = new URLSearchParams(search);

  const updateQuery = (params: Record<string, string | number>) => {
    const newQuery = new URLSearchParams(search);
    Object.entries(params).forEach(([key, value]) => {
      newQuery.set(key, String(value));
    });
    history.push(`${pathname}?${newQuery.toString()}`);
  };

  return [query, updateQuery] as const;
}

export const useInvoiceService = () => {
  // the purpose of this hook is to unify calls to the invoice service, and share state around loading and errors
  const [invoiceList, setInvoiceList] = useState<InvoiceList>({
    invoices: [],
    pagination: {
      pageNumber: 0,
      pageSize: 20,
      total: 0
    },
    filters: {
      status: null,
      shippingLocation: null
    },
    sorting: {
      sortBy: SortableColumnKeys.DueDate,
      sortDirection: SortOrder.ASC
    }
  });

  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  const [creditList, setCreditList] = useState<CreditList>({
    credits: [],
    pagination: {
      pageNumber: 0,
      pageSize: 20,
      total: 0
    },
    sorting: {
      sortBy: CreditSortableColumnKeys.CreditDate,
      sortDirection: SortOrder.ASC
    }
  });
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  const { userInfo } = useUserInfoContext() as {
    userInfo: { userId: string };
    setUserInfo: Dispatch<SetStateAction<{ userId: string }>>;
  };
  const userId = userInfo?.userId;

  const [searchParams, updateSearchParams] = useQueryParams();

  const creditsEnabled = useFeatureFlag("SEE_AND_APPLY_CREDITS");
  const history = useHistory();

  const tabs = useMemo(() => {
    const availableTabs = [
      {
        id: InvoicesAndPaymentsTabs.invoices.id,
        label: InvoicesAndPaymentsTabs.invoices.label
      }
    ];
    if (creditsEnabled)
      availableTabs.push({
        id: InvoicesAndPaymentsTabs.credits.id,
        label: InvoicesAndPaymentsTabs.credits.label
      });
    return availableTabs;
  }, [creditsEnabled]);

  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    const validTab = tabs.find((t) => t.id === tabFromUrl);

    if (validTab) return validTab.id;
    return tabs[0]?.id || InvoicesAndPaymentsTabs.invoices.id;
  });

  const onClickTab = (tabId: string) => {
    setActiveTab(tabId);

    const params = new URLSearchParams();
    params.set("tab", tabId);

    history.replace({
      pathname: location.pathname,
      search: params.toString()
    });
  };

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(
    searchParams.get("pageSize") || "10",
    10
  ) as PageSize;

  const defaultSortProperty =
    activeTab === InvoicesAndPaymentsTabs.invoices.id
      ? SortableColumnKeys.DueDate
      : CreditSortableColumnKeys.CreditDate;
  const sortProperty = (searchParams.get("sort") || defaultSortProperty) as
    | SortableColumnKeys
    | CreditSortableColumnKeys;
  const sortDirection = (searchParams.get("direction") || "asc") as SortOrder;

  const fetchInvoices = async () => {
    setIsLoadingInvoices(true);
    setInvoiceError(null);

    try {
      const queryParams = new URLSearchParams();

      if (currentPage !== undefined)
        queryParams.set("pageNumber", String(currentPage - 1)); // API is 0-indexed
      if (pageSize !== undefined) queryParams.set("pageSize", String(pageSize));
      if (sortProperty) queryParams.set("sortProperty", sortProperty);
      if (sortDirection) queryParams.set("sortDirection", sortDirection);

      const response = await authAxiosInstance({
        url: `/invoiceservice/api/v1/invoices/search?${queryParams.toString()}`,
        method: "POST",
        data: { userId }
      });

      const data = response.data;

      // handle weird case where someone loads a page number with no data by resetting to page 1
      if (data.pagination.total > 0 && data.invoices.length === 0) {
        updateSearchParams({
          page: 1
        });
        return;
      }

      // UNCOMMENT TO USE MOCK DATA FOR DEBUGGING
      // const data: InvoiceList = {
      //   invoices: mockInvoiceList.slice(
      //     (currentPage - 1) * pageSize,
      //     currentPage * pageSize
      //   ),
      //   pagination: {
      //     pageNumber: currentPage,
      //     pageSize: pageSize,
      //     total: mockInvoiceList.length
      //   },
      //   filters: {
      //     status: null,
      //     shippingLocation: null
      //   },
      //   sorting: {
      //     sortBy: sortProperty,
      //     sortDirection: sortDirection
      //   }
      // };

      setInvoiceList(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching invoices:", err);
      setInvoiceError((err as Error).message);
      setInvoiceError(getErrorMessage(ErrorTypes.InvoiceErrors, err));
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const fetchCredits = async () => {
    setIsLoadingCredits(true);
    setCreditError(null);

    try {
      const queryParams = new URLSearchParams();

      if (currentPage !== undefined)
        queryParams.set("pageNumber", String(currentPage - 1));
      if (pageSize !== undefined) queryParams.set("pageSize", String(pageSize));
      if (sortProperty) queryParams.set("sortProperty", sortProperty);
      if (sortDirection) queryParams.set("sortDirection", sortDirection);

      const response = await authAxiosInstance({
        url: `/invoiceservice/api/v1/credits/search?${queryParams.toString()}`,
        method: "POST",
        data: {
          userId
        }
      });

      const data = response.data;

      // Fallback: reset to page 1 if no results
      if (data.pagination.total > 0 && data.credits.length === 0) {
        updateSearchParams({
          page: 1
        });
        return;
      }

      setCreditList(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching credits:", err);
      setCreditError((err as Error).message);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  useEffect(() => {
    if (userId && activeTab === InvoicesAndPaymentsTabs.invoices.id) {
      fetchInvoices();
    } else {
      fetchCredits();
    }
    // we don't want to callbackify fetchData so we have to keep it out of the dependencies, this is strategic so that we only call it on mount or when url params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeTab, currentPage, pageSize, sortProperty, sortDirection]);

  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage });
  };

  const handleSort = (sort: SortableColumn | CreditSortableColumn) => {
    updateSearchParams({
      sort: sort.property,
      direction: sort.direction,
      page: 1 // reset page on sort
    });
  };

  const handlePageSizeChange = (size: PageSize) => {
    updateSearchParams({
      pageSize: size,
      page: 1 // reset page on size change
    });
  };

  return {
    // Common
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // Invoices
    invoiceList: invoiceList,
    isLoadingInvoices,
    invoiceError,
    sortProperty,
    sortDirection,
    handleSort,

    // Credits
    creditList,
    isLoadingCredits,
    creditError,

    // Tabs
    tabs,
    activeTab,
    onClickTab
  };
};
